import type { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Card, CardActionArea, Grid, LinearProgress, Typography } from '@mui/material';

import { fShortenNumber } from "../../utils/formatNumber";

const StyledCover = styled('img')({
    display: 'block',
    width: '100%',
    aspectRatio: '4 / 3',
    objectFit: 'cover',
});

const StyledPreviewBadge = styled('span')(({ theme }) => ({
    position: 'absolute',
    top: 10,
    left: 10,
    padding: '4px 8px',
    borderRadius: 6,
    backgroundColor: '#FFC940',
    color: theme.palette.grey[900],
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.04em',
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
    overflow: 'hidden',
    WebkitLineClamp: 2,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    fontSize: 16,
    lineHeight: '22px',
    fontWeight: 700,
    color: theme.palette.grey[900],
}));

/** What the number next to the title counts: questions for a workout definition, lessons for a course. */
export type CatalogCardCounter = 'questions' | 'lessons';

/** The card renders both courses and workout definitions, so size/rate are optional. */
export type CatalogCardItem = {
    cover: string;
    title: string;
    size?: number;
    counter?: CatalogCardCounter;
    rate?: number;
    /** Marks an entry that does not have enough questions to be practiced seriously yet. */
    preview?: boolean;
    /** A line under the title, e.g. the roadmaps a roadmap builds on. */
    subtitle?: ReactNode;
};

export type CatalogCardProps = {
    item: CatalogCardItem;
    onClick?: () => void;
};

/** Only workouts are answered, so only they show the rate of correct answers. */
function hasRate(counter: CatalogCardCounter) {
    return counter === 'questions';
}

export function CatalogCard({ item, onClick }: CatalogCardProps) {
    const { cover, title, size, counter = 'questions', rate, preview, subtitle } = item;

    return (
        <Grid item xs={ 12 } sm={ 6 } md={ 4 } lg={ 3 }>
            <Card sx={ {
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                border: '1px solid #E3E8EE',
                boxShadow: 'none',
            } }>
                <CardActionArea onClick={ onClick } sx={ {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                    '&:hover .catalog-card-title': { textDecoration: 'underline' },
                } }>
                    <Box sx={ { position: 'relative' } }>
                        <StyledCover alt={ title } src={ cover }/>

                        { preview && <StyledPreviewBadge>PREVIEW</StyledPreviewBadge> }
                    </Box>

                    <Box sx={ { p: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 1.25 } }>
                        <StyledTitle className="catalog-card-title">
                            { title }
                        </StyledTitle>

                        { subtitle && (
                            <Typography variant="caption" component="div" sx={ { color: 'grey.600' } }>
                                { subtitle }
                            </Typography>
                        ) }

                        { !!size && (
                            <Box sx={ { display: 'flex', justifyContent: 'space-between', gap: 1 } }>
                                <Typography variant="caption" sx={ { color: 'grey.600' } }>
                                    { fShortenNumber(size) } { counter }
                                </Typography>

                                { hasRate(counter) && (
                                    <Typography variant="caption" sx={ { color: 'grey.600' } }>
                                        { rate ? `${ rate }% correct` : 'Not practiced yet' }
                                    </Typography>
                                ) }
                            </Box>
                        ) }

                        { !!size && hasRate(counter) && (
                            <LinearProgress variant="determinate"
                                            value={ rate ?? 0 }
                                            aria-label="Rate of correct answers"
                                            sx={ {
                                                height: 6,
                                                borderRadius: 3,
                                                bgcolor: '#EDF0F3',
                                                '& .MuiLinearProgress-bar': { borderRadius: 3 },
                                            } }/>
                        ) }
                    </Box>
                </CardActionArea>
            </Card>
        </Grid>
    );
}
